import React, { useEffect } from 'react';
import { EnergyQuoteEligibility, Exceptions } from '@/src/types/EnergyQuote';
import { CTAButton, Heading3, Margin } from '@ovotech/element-v4';
import {
  EligibilityNotification,
  NotificationId,
} from '../EligibilityNotification/EligibilityNotification';
import { AnalyticsClick, useAnalytics } from '@ovotech/ui-tools';
import {
  HOME_MOVES_GET_QUOTE_TUWY,
  HOME_MOVES_TUWY_UNAVAILABLE,
} from '@/src/constants/analytics-subjects';
import styles from './TuwyEligibility.module.css';
import { MovingDateValues } from '@/src/types/MoveOut';
import { isCotMoveInTooFarInTheFuture } from '../../utils/cotMoveIn';
import { toDate } from '../../utils/movingDateValuesConversion';
type TuwyEligibilityProps = {
  data: EnergyQuoteEligibility | undefined;
  movingDateValues: MovingDateValues;
  onGetQuoteClick: () => void;
};

export const TuwyEligibility = ({
  data,
  movingDateValues,
  onGetQuoteClick,
}: TuwyEligibilityProps) => {
  const { dispatch } = useAnalytics();
  const has00meter = data?.electricityMeterInformation?.some(
    meter => meter.profileClass == '00',
  );

  const hasBlockingException = data?.exceptionsReasons?.some(ex => {
    // If has00meter is true, UnrecognisedTariffCategory and HasElectiveHalfHourlyMeter are not considered blocking.
    if (has00meter) {
      return (
        ex !== Exceptions.UnrecognisedTariffCategory &&
        ex !== Exceptions.HasElectiveHalfHourlyMeter
      );
    }
    return true;
  });

  const moveInDate = toDate(movingDateValues);
  const isCot = data?.supplier === 'OVO';
  const isFutureCotMoveIn = isCotMoveInTooFarInTheFuture(isCot, moveInDate);

  useEffect(() => {
    if (!data) {
      dispatch({
        type: 'view',
        name: HOME_MOVES_TUWY_UNAVAILABLE,
        properties: {
          reason: 'no_data',
          hasData: false,
        },
      });
    } else if (data.supportStatus === 'None' || hasBlockingException) {
      dispatch({
        type: 'view',
        name: HOME_MOVES_TUWY_UNAVAILABLE,
        properties: {
          reason:
            data.supportStatus === 'None'
              ? 'no_support'
              : 'complex_meter_setup',
          supportStatus: data.supportStatus,
          hasBlockingException,
          has00meter,
          exceptionsReasons: data.exceptionsReasons,
          currentSupplier: data.supplier,
          moveInDate: moveInDate?.toISOString(),
        },
      });
    } else if (isFutureCotMoveIn) {
      dispatch({
        type: 'view',
        name: HOME_MOVES_TUWY_UNAVAILABLE,
        properties: {
          reason: 'cot_too_far_in_future',
          isCot,
          moveInDate: moveInDate?.toISOString(),
          currentSupplier: data.supplier,
          supportStatus: data.supportStatus,
        },
      });
    }
  }, [
    data,
    hasBlockingException,
    has00meter,
    moveInDate,
    isFutureCotMoveIn,
    isCot,
    dispatch,
  ]);

  if (!data) {
    return <EligibilityNotification id={NotificationId.ERROR} />;
  }

  if (data.supportStatus === 'None' || hasBlockingException) {
    return (
      <EligibilityNotification
        id={NotificationId.HOME_HAS_COMPLEX_METER_SETUP}
      />
    );
  }

  if (isFutureCotMoveIn) {
    return (
      <EligibilityNotification id={NotificationId.COT_TOO_FAR_IN_FUTURE} />
    );
  }

  return (
    <>
      <EligibilityNotification
        id={
          isCot
            ? NotificationId.HOME_ALREADY_POWERED_BY_OVO
            : NotificationId.HOME_CAN_BE_POWERED_BY_OVO
        }
      />

      <Margin top={[6, 6, 6]}>
        <Heading3 className={styles.title}>
          See what quotes we can give you at your new home.
        </Heading3>
        <div className={styles.description}>
          By clicking “Get a quote”, you agree that we can use your new address
          to find your meter details and current supplier via industry
          databases.
        </div>
      </Margin>

      <Margin top={[5, 6, 6]}>
        <AnalyticsClick
          name={HOME_MOVES_GET_QUOTE_TUWY}
          properties={{
            newSupplier: data?.supplier,
            isCot,
            supportStatus: data?.supportStatus,
            hasComplexMeter: has00meter,
            moveInDate: moveInDate?.toISOString(),
          }}
        >
          <CTAButton
            type="button"
            onClick={() => {
              onGetQuoteClick();
            }}
          >
            Get a quote
          </CTAButton>
        </AnalyticsClick>
      </Margin>
    </>
  );
};
